import { supabase } from '@/lib/supabase';
import type {
  ProspectsInsert,
  ProspectsRow,
  ProspectsUpdate,
  ProspectSharesRow,
  ProspectSharesInsert,
} from '@/types/database.types';

import type { ProspectFormValues, ShareProspectValues } from './schema';

/**
 * Capa de acceso a `core.*` para el feature prospects.
 *
 * - Lectura/escritura va por `supabase` (schema 'core' por default).
 * - RLS valida en Postgres — el cliente solo manda queries.
 * - `empty string → null` para campos opcionales antes de enviar.
 */

export interface ProspectListFilters {
  search?: string;
  status?: ProspectsRow['status'];
  ownerOnly?: boolean;
  /**
   * Filtrar por owner_user_id. Solo lo usa superadmin desde la UI; los
   * usuarios sin bypass ya ven solo lo suyo via RLS y este filtro es ruido.
   */
  ownerUserId?: string;
  /** Orden del listado. Por defecto, los más nuevos primero. */
  sort?: ProspectSort;
  /** Fecha de alta, desde (inclusive), formato yyyy-mm-dd. */
  createdFrom?: string;
  /** Fecha de alta, hasta (inclusive), formato yyyy-mm-dd. */
  createdTo?: string;
}

export type ProspectSort = 'created_desc' | 'created_asc' | 'updated_desc' | 'name_asc';

export interface ProspectListResult {
  rows: ProspectsRow[];
  count: number;
}

const LIST_PAGE_SIZE = 25;

const SORT_COLUMN: Record<ProspectSort, string> = {
  created_desc: 'created_at',
  created_asc: 'created_at',
  updated_desc: 'updated_at',
  name_asc: 'name',
};

function emptyToNull(value: string | undefined | null): string | null {
  if (value === undefined || value === null) return null;
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

/**
 * Sanea un término de búsqueda antes de interpolarlo en un filtro PostgREST
 * `.or('col.ilike.%<term>%')`. Sin esto, una búsqueda con `, ( ) *` rompe la
 * gramática del filtro (filter injection → resultados erróneos; RLS sigue
 * aplicando, así que no es fuga de datos). También escapamos `%` y `\` para
 * que no actúen como comodines del LIKE.
 */
function sanitizeSearchTerm(term: string): string {
  return term.replace(/[,()*%\\]/g, ' ').trim();
}

function formToInsert(
  values: ProspectFormValues,
  ownerUserId: string,
): ProspectsInsert {
  return {
    owner_user_id: ownerUserId,
    name: values.name.trim(),
    legal_name: emptyToNull(values.legal_name),
    tax_id: emptyToNull(values.tax_id),
    website: emptyToNull(values.website),
    address: emptyToNull(values.address),
    city: emptyToNull(values.city),
    province: emptyToNull(values.province),
    country_code: emptyToNull(values.country_code),
    primary_contact_name: emptyToNull(values.primary_contact_name),
    primary_contact_email: emptyToNull(values.primary_contact_email),
    whatsapp: emptyToNull(values.whatsapp),
    linkedin_company: emptyToNull(values.linkedin_company),
    instagram: emptyToNull(values.instagram),
    sector: emptyToNull(values.sector),
    scale: emptyToNull(values.scale),
    business_context: emptyToNull(values.business_context),
    pain_point: emptyToNull(values.pain_point),
    opportunity: emptyToNull(values.opportunity),
    proposed_solution: emptyToNull(values.proposed_solution),
    recommended_approach: emptyToNull(values.recommended_approach),
    estimated_deal_usd: values.estimated_deal_usd ?? null,
    estimated_cycle_days: values.estimated_cycle_days ?? null,
    close_probability: values.close_probability ?? null,
    status: values.status,
    priority: emptyToNull(values.priority),
    internal_notes: emptyToNull(values.internal_notes),
    created_by: ownerUserId,
    updated_by: ownerUserId,
    // Campos no editables desde el form (defaults)
    ceo: null,
    facebook: null,
    twitter: null,
    pain_evidence: null,
    pain_quotes: null,
    motivation: null,
    raw_research_notes: null,
    research_summary: null,
    research_verified_at: null,
    research_verified_by: null,
    deleted_at: null,
    deleted_by: null,
    migrated_from: null,
    migrated_at: null,
    legacy_id: null,
    score: null,
    opportunity_score: null,
  };
}

function formToUpdate(
  values: ProspectFormValues,
  updaterUserId: string,
): ProspectsUpdate {
  return {
    name: values.name.trim(),
    legal_name: emptyToNull(values.legal_name),
    tax_id: emptyToNull(values.tax_id),
    website: emptyToNull(values.website),
    address: emptyToNull(values.address),
    city: emptyToNull(values.city),
    province: emptyToNull(values.province),
    country_code: emptyToNull(values.country_code),
    primary_contact_name: emptyToNull(values.primary_contact_name),
    primary_contact_email: emptyToNull(values.primary_contact_email),
    whatsapp: emptyToNull(values.whatsapp),
    linkedin_company: emptyToNull(values.linkedin_company),
    instagram: emptyToNull(values.instagram),
    sector: emptyToNull(values.sector),
    scale: emptyToNull(values.scale),
    business_context: emptyToNull(values.business_context),
    pain_point: emptyToNull(values.pain_point),
    opportunity: emptyToNull(values.opportunity),
    proposed_solution: emptyToNull(values.proposed_solution),
    recommended_approach: emptyToNull(values.recommended_approach),
    estimated_deal_usd: values.estimated_deal_usd ?? null,
    estimated_cycle_days: values.estimated_cycle_days ?? null,
    close_probability: values.close_probability ?? null,
    status: values.status,
    priority: emptyToNull(values.priority),
    internal_notes: emptyToNull(values.internal_notes),
    updated_at: new Date().toISOString(),
    updated_by: updaterUserId,
  };
}

export function rowToForm(row: ProspectsRow): ProspectFormValues {
  return {
    name: row.name,
    legal_name: row.legal_name ?? '',
    tax_id: row.tax_id ?? '',
    website: row.website ?? '',
    address: row.address ?? '',
    city: row.city ?? '',
    province: row.province ?? '',
    country_code: row.country_code ?? '',
    primary_contact_name: row.primary_contact_name ?? '',
    primary_contact_email: row.primary_contact_email ?? '',
    whatsapp: row.whatsapp ?? '',
    linkedin_company: row.linkedin_company ?? '',
    instagram: row.instagram ?? '',
    sector: row.sector ?? '',
    scale: row.scale ?? '',
    business_context: row.business_context ?? '',
    pain_point: row.pain_point ?? '',
    opportunity: row.opportunity ?? '',
    proposed_solution: row.proposed_solution ?? '',
    recommended_approach: row.recommended_approach ?? '',
    estimated_deal_usd: row.estimated_deal_usd ?? undefined,
    estimated_cycle_days: row.estimated_cycle_days ?? undefined,
    close_probability: row.close_probability ?? undefined,
    status: row.status,
    priority: row.priority ?? '',
    internal_notes: row.internal_notes ?? '',
  };
}

// ---------------------------------------------------------------------------
// List
// ---------------------------------------------------------------------------

export async function listProspects(
  filters: ProspectListFilters,
  page: number,
  signal?: AbortSignal,
): Promise<ProspectListResult> {
  const from = page * LIST_PAGE_SIZE;
  const to = from + LIST_PAGE_SIZE - 1;
  const sort = filters.sort ?? 'created_desc';

  let query = supabase
    .from('prospects')
    .select('*', { count: 'exact' })
    .eq('deleted', false)
    .order(SORT_COLUMN[sort], { ascending: sort === 'created_asc' || sort === 'name_asc' })
    // Desempate obligatorio: 147 fichas comparten created_at (importaciones en
    // lote). Sin un orden total, Postgres puede devolver los empates en otro
    // orden en cada consulta y con `range` la misma fila cae en dos páginas.
    .order('id', { ascending: true })
    .range(from, to);
  // Al pasar de página rápido, TanStack cancela la consulta anterior.
  if (signal) query = query.abortSignal(signal);

  if (filters.search && filters.search.trim().length > 0) {
    // Saneamos los chars con significado en la gramática de PostgREST/LIKE
    // antes de interpolar (evita filter injection).
    const s = sanitizeSearchTerm(filters.search);
    if (s.length > 0) {
      // Search en name / legal_name / city / primary_contact_email
      query = query.or(
        `name.ilike.%${s}%,legal_name.ilike.%${s}%,city.ilike.%${s}%,primary_contact_email.ilike.%${s}%`,
      );
    }
  }
  if (filters.status) {
    query = query.eq('status', filters.status);
  }
  if (filters.ownerUserId) {
    query = query.eq('owner_user_id', filters.ownerUserId);
  }
  // Las fechas son de calendario local: el día «hasta» entra completo.
  if (filters.createdFrom) {
    query = query.gte('created_at', new Date(`${filters.createdFrom}T00:00:00`).toISOString());
  }
  if (filters.createdTo) {
    const fin = new Date(`${filters.createdTo}T00:00:00`);
    fin.setDate(fin.getDate() + 1);
    query = query.lt('created_at', fin.toISOString());
  }

  const { data, error, count } = await query;
  if (error) throw error;
  return { rows: data ?? [], count: count ?? 0 };
}

// ---------------------------------------------------------------------------
// Options (picker liviano)
// ---------------------------------------------------------------------------

export interface ProspectOption {
  id: string;
  name: string;
}

/**
 * Lista TODOS los prospects activos como opciones livianas (id + name) para
 * pickers/comboboxes. A diferencia de `listProspects` NO pagina (el listado
 * pagina de a 25, lo que dejaba afuera la mayoría de los prospects al
 * vincularlos desde una tarea). Para < 5k filas es barato traerlos todos —
 * mismo criterio que `fetchProspectsStatusCounts`. Si crece, mover a RPC.
 */
export async function listProspectOptions(): Promise<ProspectOption[]> {
  const { data, error } = await supabase
    .from('prospects')
    .select('id, name')
    .eq('deleted', false)
    .order('name', { ascending: true });
  if (error) throw error;
  return (data ?? []).map((p) => ({
    id: p.id as string,
    name: (p.name as string | null) ?? '(sin nombre)',
  }));
}

// ---------------------------------------------------------------------------
// Recientes ("vistos por mí")
// ---------------------------------------------------------------------------

const RECENT_LIMIT = 10;

/**
 * Registra (o refresca) que el usuario actual abrió este prospecto. Upsert por
 * (user_id, prospect_id) → una fila por prospecto, viewed_at se actualiza en
 * cada apertura. Best-effort: si falla (sin sesión, RLS, red), se ignora en
 * silencio — no debe romper la vista del detalle.
 */
export async function recordProspectView(prospectId: string): Promise<void> {
  const { data } = await supabase.auth.getSession();
  const userId = data.session?.user?.id;
  if (!userId) return;
  const { error } = await supabase.from('prospect_views').upsert(
    {
      user_id: userId,
      prospect_id: prospectId,
      viewed_at: new Date().toISOString(),
    },
    { onConflict: 'user_id,prospect_id' },
  );
  if (error) {
    // best-effort, no propagamos
    console.warn('[prospects] no se pudo registrar la vista:', error.message);
  }
}

/**
 * Últimos prospectos abiertos por el usuario actual, ordenados por recencia.
 * El join `!inner` a prospects aplica la RLS de prospects: si perdiste acceso
 * a uno, no aparece. Soft-deleted se filtran client-side.
 */
export async function listRecentProspects(): Promise<ProspectsRow[]> {
  const { data, error } = await supabase
    .from('prospect_views')
    .select('viewed_at, prospect:prospects!inner(*)')
    .order('viewed_at', { ascending: false })
    .limit(RECENT_LIMIT);
  if (error) throw error;
  return ((data ?? []) as unknown as Array<{ prospect: ProspectsRow | null }>)
    .map((r) => r.prospect)
    .filter((p): p is ProspectsRow => !!p && !p.deleted);
}

// ---------------------------------------------------------------------------
// Detail
// ---------------------------------------------------------------------------

export async function getProspect(id: string): Promise<ProspectsRow> {
  const { data, error } = await supabase
    .from('prospects')
    .select('*')
    .eq('id', id)
    .single();
  if (error) throw error;
  return data;
}

// ---------------------------------------------------------------------------
// Create
// ---------------------------------------------------------------------------

export async function createProspect(
  values: ProspectFormValues,
  ownerUserId: string,
): Promise<ProspectsRow> {
  const insert = formToInsert(values, ownerUserId);
  const { data, error } = await supabase
    .from('prospects')
    .insert(insert)
    .select('*')
    .single();
  if (error) throw error;
  return data;
}

// ---------------------------------------------------------------------------
// Update
// ---------------------------------------------------------------------------

export async function updateProspect(
  id: string,
  values: ProspectFormValues,
  updaterUserId: string,
): Promise<ProspectsRow> {
  const update = formToUpdate(values, updaterUserId);
  const { data, error } = await supabase
    .from('prospects')
    .update(update)
    .eq('id', id)
    .select('*')
    .single();
  if (error) throw error;
  return data;
}

/**
 * Update parcial de campos sueltos del prospecto. Usado para aplicar
 * propuestas de IA (pain_point / opportunity) y para la edición inline del
 * detalle (texto largo + las 3 estimaciones numéricas de la propuesta) sin
 * pasar por el form completo.
 */
export async function patchProspectFields(
  id: string,
  patch: Partial<
    Pick<
      ProspectsUpdate,
      | 'pain_point'
      | 'opportunity'
      | 'business_context'
      | 'internal_notes'
      | 'proposed_solution'
      | 'recommended_approach'
      | 'estimated_deal_usd'
      | 'estimated_cycle_days'
      | 'close_probability'
      // Datos de empresa (editables desde el modal del detalle).
      | 'legal_name'
      | 'tax_id'
      | 'sector'
      | 'scale'
      | 'website'
      | 'address'
      | 'city'
      | 'province'
      | 'country_code'
      // Datos fiscales para facturación ARCA (FACT-01 · Factura C).
      | 'iva_condition_id'
      | 'fiscal_doc_tipo'
      // Datos fiscales para facturar al exterior (factura E).
      | 'fex_dst_pais'
      | 'fex_cuit_pais'
    >
  >,
  updaterUserId: string,
): Promise<ProspectsRow> {
  const { data, error } = await supabase
    .from('prospects')
    .update({
      ...patch,
      updated_at: new Date().toISOString(),
      updated_by: updaterUserId,
    })
    .eq('id', id)
    .select('*')
    .single();
  if (error) throw error;
  return data;
}

/**
 * Update parcial · solo cambia status (usado por Kanban drag&drop).
 * Más eficiente que `updateProspect` full porque solo manda 3 campos.
 */
export async function updateProspectStatus(
  id: string,
  status: ProspectsRow['status'],
  updaterUserId: string,
): Promise<ProspectsRow> {
  const { data, error } = await supabase
    .from('prospects')
    .update({
      status,
      updated_at: new Date().toISOString(),
      updated_by: updaterUserId,
    })
    .eq('id', id)
    .select('*')
    .single();
  if (error) throw error;
  return data;
}

/**
 * Reasigna el agente comercial (owner) del prospecto. Solo write.all: la
 * reasignación es un acto de gestión.
 *
 * Quien lo garantiza NO es la policy. El WITH CHECK de `prospects_update` es
 * idéntico a su USING, tiene una rama por share ('edit') que acepta cualquier
 * owner, y además no puede ver OLD. El candado real es el trigger
 * `aa_prospects_guard_owner_change` (migración 20260824030000, backlog S-11):
 * cambiar owner_user_id exige prospects.write.all, salvo backend
 * (service_role o auth.uid() NULL).
 *
 * OJO: ese guard cubre SOLO owner_user_id. La misma rama write.shared sigue
 * aceptando escrituras sobre `deleted` e `is_client` — pendiente en backlog.
 */
export async function reassignProspectOwner(
  id: string,
  ownerUserId: string,
  updaterUserId: string,
): Promise<ProspectsRow> {
  const { data, error } = await supabase
    .from('prospects')
    .update({
      owner_user_id: ownerUserId,
      updated_at: new Date().toISOString(),
      updated_by: updaterUserId,
    })
    .eq('id', id)
    .select('*')
    .single();
  if (error) throw error;
  return data;
}

// ---------------------------------------------------------------------------
// Soft delete (set deleted = true)
// ---------------------------------------------------------------------------

export async function softDeleteProspect(
  id: string,
  deleterUserId: string,
): Promise<void> {
  const now = new Date().toISOString();
  const update: ProspectsUpdate = {
    deleted: true,
    deleted_at: now,
    deleted_by: deleterUserId,
    updated_at: now,
    updated_by: deleterUserId,
  };
  const { error } = await supabase
    .from('prospects')
    .update(update)
    .eq('id', id);
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Shares
// ---------------------------------------------------------------------------

export async function listProspectShares(
  prospectId: string,
): Promise<ProspectSharesRow[]> {
  const { data, error } = await supabase
    .from('prospect_shares')
    .select('*')
    .eq('prospect_id', prospectId)
    .order('shared_at', { ascending: false });
  if (error) throw error;
  return data ?? [];
}

/**
 * Resuelve un email a UUID consultando `core.find_user_id_by_email`.
 * Devuelve `null` si no existe usuario con ese email.
 */
export async function resolveUserIdByEmail(
  email: string,
): Promise<string | null> {
  const { data, error } = await supabase
    .schema('core')
    .rpc('find_user_id_by_email', { p_email: email });
  if (error) throw error;
  return data ?? null;
}

/**
 * Lookup error: el email proporcionado no corresponde a ningún usuario.
 * Useful para que el UI pueda diferenciar "usuario inexistente" de otros errores.
 */
export class UserNotFoundError extends Error {
  email: string;
  constructor(email: string) {
    super(`No existe un usuario con email ${email}`);
    this.name = 'UserNotFoundError';
    this.email = email;
  }
}

export async function shareProspect(
  prospectId: string,
  values: ShareProspectValues,
  sharedByUserId: string,
): Promise<ProspectSharesRow> {
  // 1. Resolver email → UUID
  const userId = await resolveUserIdByEmail(values.email);
  if (!userId) throw new UserNotFoundError(values.email);

  // 2. Insertar share
  const insert: ProspectSharesInsert = {
    prospect_id: prospectId,
    shared_with_user_id: userId,
    permission: values.permission,
    shared_by: sharedByUserId,
    notes: emptyToNull(values.notes),
  };
  const { data, error } = await supabase
    .from('prospect_shares')
    .insert(insert)
    .select('*')
    .single();
  if (error) throw error;
  return data;
}

export async function revokeShare(
  prospectId: string,
  sharedWithUserId: string,
): Promise<void> {
  const { error } = await supabase
    .from('prospect_shares')
    .delete()
    .eq('prospect_id', prospectId)
    .eq('shared_with_user_id', sharedWithUserId);
  if (error) throw error;
}
